import {
  QuoteSheetDTO,
  QuoteSheetLineItemAvailability,
} from '@/api/procurement/quote-sheets-types';
import { getProductName } from '@/components/procurement/utils';
import { ProductV2PillV2 } from '@/components/products/ProductV2PillV2';
import { useMemo } from 'react';

export function getQuoteSheetProductMatches(
  quoteSheet: QuoteSheetDTO,
  filter = '',
) {
  const productCounts = new Map<string, number>();

  quoteSheet.lineItems
    ?.filter((lineItem) => {
      const matchesFilter =
        filter &&
        getProductName(lineItem).toLowerCase().includes(filter.toLowerCase());

      if (filter) {
        return matchesFilter;
      }

      return (
        (lineItem.suggestedProductVariantId ||
          lineItem.selectedProductId ||
          lineItem.suggestedSupplierProductId ||
          lineItem.selectedSupplierProductId) &&
        lineItem.availability === QuoteSheetLineItemAvailability.AVAILABLE
      );
    })
    .forEach((lineItem) => {
      const productName =
        lineItem.suggestedProduct?.name ??
        String(lineItem.rawData?.productName);
      productCounts.set(productName, (productCounts.get(productName) || 0) + 1);
    });

  return Array.from(productCounts.entries()).map(([name, count]) =>
    count > 1 ? `${name} (${count})` : name,
  );
}

function useQuoteSheetProducts(quoteSheet: QuoteSheetDTO, filter = '') {
  const matchedProducts = useMemo(
    () => getQuoteSheetProductMatches(quoteSheet, filter),
    [quoteSheet, filter],
  );

  const numUnknownProducts =
    (quoteSheet.lineItems?.length ?? 0) - (matchedProducts?.length ?? 0);

  return {
    matchedProducts,
    numUnknownProducts,
  };
}

export function QuoteSheetInboxProductsView({
  quoteSheet,
  filter,
}: {
  quoteSheet: QuoteSheetDTO;
  filter?: string;
}) {
  const { matchedProducts, numUnknownProducts } = useQuoteSheetProducts(
    quoteSheet,
    filter,
  );

  return (
    <div className="flex gap-2">
      {matchedProducts?.map((lineItemName, i) => (
        <ProductV2PillV2 key={`${lineItemName}-${i}`} name={lineItemName} />
      ))}
      {numUnknownProducts > 0 && (
        <div className="text-sm text-gray-500">
          <ProductV2PillV2 name={`+${numUnknownProducts}`} />
        </div>
      )}
    </div>
  );
}
